<template>
    <div
        ref="root"
        :class="[$style.root, adaptiveSize && $style.root_fill]"
        :style="rootStyle"
        :data-fills-slot="adaptiveSize || undefined"
    >
        <div
            :id="id"
            :class="$style.slot"
            :style="slotStyle"
        />
        <button
            v-if="isFallbackVisible"
            type="button"
            :class="$style.fallback"
            @click="onFallbackClick"
        >
            <img
                :class="$style.fallback_image"
                :src="fallbackImage"
                :alt="tr('adFreeBanners.noAds')"
            />
        </button>
    </div>
</template>
<script lang="ts" setup>
import { useResizeObserver, useThrottleFn } from '@vueuse/core'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useId, useTemplateRef, watch } from 'vue'

import { useTr } from '@/composables/use-tr'
import type { PageAd } from '@/modules/adv/page-ad'
import type { NativeBannerSize, PageAdType } from '@/modules/adv/types'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'
import { useLayoutStore } from '@/store/layout-store'

import noAdsBanner from './images/no_ads2x.webp'
import noAdsHigherBanner from './images/no_ads_higher2x.webp'
import {
    fitsRenderedBox,
    getFallbackRule,
    hasRenderedBox,
    isElementCovered,
    matchesFullscreenMode,
} from './native-banner-rules'
import { getNativeAdUnitDonor, getNativeBannerSlot, pickAdaptiveBannerSize } from './native-banners-config'

const props = defineProps<{ placement: PageAdType }>()

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()

const COVERAGE_POLL_MS = 1000

type FallbackAction = 'ad-free-modal'

type FallbackCreative = {
    image: string
    action?: FallbackAction
}

const FALLBACK_CREATIVES: Record<string, FallbackCreative> = {
    '728x90': { image: noAdsBanner, action: 'ad-free-modal' },
    '320x50': { image: noAdsBanner, action: 'ad-free-modal' },
    '320x90': { image: noAdsHigherBanner, action: 'ad-free-modal' },
}

const adsStore = useAdsStore()
const adDisabledStore = useAdDisabledStore()
const layoutStore = useLayoutStore()
const tr = useTr()

const id = useId()
const rootRef = useTemplateRef('root')
const banner = shallowRef<PageAd>()
const isCovered = ref(false)
const hasBox = ref(false)
const hasRoom = ref(false)
const hasNoFill = ref(false)
const isRendered = ref(false)

const pollId = ref<number>()

const viewportWidth = ref(import.meta.env.SSR ? 0 : window.innerWidth)
const viewportHeight = ref(import.meta.env.SSR ? 0 : window.innerHeight)

const nativeSlot = computed(() =>
    getNativeBannerSlot(props.placement, { width: viewportWidth.value, height: viewportHeight.value }),
)

const adaptiveSize = computed(() => !!nativeSlot.value?.adaptiveSize)

const availableBox = ref({ width: 0, height: 0 })

const slotSizes = computed<NativeBannerSize[]>(() => {
    const sizes = nativeSlot.value?.sizes ?? []

    if (!adaptiveSize.value) {
        return sizes
    }

    const best = pickAdaptiveBannerSize(sizes, availableBox.value)
    return best ? [best] : []
})

const box = computed(() => ({
    width: Math.max(0, ...slotSizes.value.map(([width]) => width)),
    height: Math.max(0, ...slotSizes.value.map(([, height]) => height)),
}))

const boxKey = computed(() => `${box.value.width}x${box.value.height}`)

const adUnitDonor = computed(() => getNativeAdUnitDonor(props.placement, slotSizes.value))

const requestedAdUnitDonor = ref<PageAdType>()

const boxStyle = computed(() => ({ width: `${box.value.width}px`, height: `${box.value.height}px` }))

const rootStyle = computed(() => (adaptiveSize.value ? undefined : boxStyle.value))
const slotStyle = computed(() => (adaptiveSize.value ? boxStyle.value : undefined))

const fallbackRule = computed(() => getFallbackRule(props.placement))

const fallbackCreative = computed(() => FALLBACK_CREATIVES[`${box.value.width}x${box.value.height}`])

const fallbackImage = computed(() => fallbackCreative.value?.image)

const fallbackAction = computed(() => fallbackCreative.value?.action)

const isFallbackVisible = computed(
    () =>
        !!fallbackCreative.value &&
        ((hasNoFill.value && !!fallbackRule.value?.noFill) ||
            (hasBox.value && !hasRoom.value && !!fallbackRule.value?.noRoom)),
)

const isVisible = computed(() => isRendered.value || isFallbackVisible.value)

const matchesFullscreen = computed(() => matchesFullscreenMode(props.placement, layoutStore.fullScreen))

const canShowBanner = computed(
    () =>
        hasRoom.value &&
        !!nativeSlot.value &&
        (!adaptiveSize.value || slotSizes.value.length > 0) &&
        !!adsStore.adService &&
        !adDisabledStore.isAdDisabled &&
        matchesFullscreen.value &&
        hasBox.value &&
        !isCovered.value,
)

function onFallbackClick() {
    if (fallbackAction.value === 'ad-free-modal') {
        adDisabledStore.openDisabledAdModal('ad-slot-fallback', props.placement)
    }
}

function measureBox() {
    const el = rootRef.value
    if (!el) {
        return
    }

    viewportWidth.value = window.innerWidth
    viewportHeight.value = window.innerHeight
    hasBox.value = hasRenderedBox(el)

    // Feeds `box` below, so it has to settle before the room check reads the chosen size.
    if (adaptiveSize.value) {
        const rect = el.getBoundingClientRect()
        availableBox.value = { width: rect.width, height: rect.height }
    }

    hasRoom.value = hasBox.value && fitsRenderedBox(el, box.value.width)
    isCovered.value = hasBox.value && isElementCovered(el)

    schedulePositionUpdate()
}

let positionUpdatePending = false

function schedulePositionUpdate() {
    if (positionUpdatePending) {
        return
    }
    positionUpdatePending = true

    nextTick(() => {
        positionUpdatePending = false

        // A donor change replaces the slot rather than resizing it; the old one must never get the new format.
        if (!banner.value || !canShowBanner.value || requestedAdUnitDonor.value !== adUnitDonor.value) {
            return
        }

        banner.value.updatePosition(slotSizes.value)
    })
}

const measureBoxThrottled = useThrottleFn(measureBox, 200)

function reportNoFill() {
    hasNoFill.value = true
    isRendered.value = false
}

function requestBanner() {
    if (banner.value || !adsStore.adService || !nativeSlot.value) {
        return
    }

    hasNoFill.value = false
    isRendered.value = false
    requestedAdUnitDonor.value = adUnitDonor.value

    const publicBanner = adsStore.adService.requestPageAd({
        placement: props.placement,
        el: id,
        sizes: slotSizes.value,
        nativeBanner: {
            coordinates: nativeSlot.value.coordinates,
            adUnitDonor: adUnitDonor.value,
            adaptive: nativeSlot.value.adaptive,
            sizes: nativeSlot.value.sizes,
        },
    })

    publicBanner.addEventListener('rendered', () => {
        hasNoFill.value = false
        isRendered.value = true
    })

    publicBanner.addEventListener('native_no_fill', reportNoFill)
    publicBanner.addEventListener('empty', destroyBanner)
    publicBanner.addEventListener('closed', destroyBanner)
    publicBanner.addEventListener('destroyed', destroyBanner)
    publicBanner.addEventListener('failed', destroyBanner)

    banner.value = publicBanner
}

function destroyBanner() {
    if (!banner.value) {
        return
    }

    banner.value.destroy()
    banner.value = undefined
    hasNoFill.value = false
    isRendered.value = false
}

function syncBanner() {
    if (canShowBanner.value) {
        requestBanner()
    } else {
        destroyBanner()
    }
}

watch(canShowBanner, syncBanner)

watch(boxKey, () => {
    if (!banner.value) {
        return
    }

    if (adUnitDonor.value !== requestedAdUnitDonor.value) {
        destroyBanner()
        syncBanner()
        return
    }

    schedulePositionUpdate()
})

watch(isVisible, (value) => emit('update:visible', value))

// Entering fullscreen puts an overlay over the page without resizing anything.
watch(
    () => layoutStore.fullScreen,
    () => nextTick(measureBox),
)

useResizeObserver(rootRef, () => {
    measureBoxThrottled()
})

onMounted(() => {
    measureBox()
    syncBanner()
    pollId.value = window.setInterval(measureBox, COVERAGE_POLL_MS)
    window.addEventListener('resize', measureBoxThrottled, { passive: true })
})

onBeforeUnmount(() => {
    window.clearInterval(pollId.value)
    window.removeEventListener('resize', measureBoxThrottled)
    destroyBanner()
})
</script>
<style module>
.root {
    position: relative;
    max-width: 100%;
    /* Hit-testable on purpose: coverage detection relies on this box answering elementFromPoint. */
    pointer-events: auto;
}

.root_fill {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.slot {
    width: 100%;
    height: 100%;
}

.fallback {
    position: absolute;
    inset: 0;
    padding: 0;
    border: none;
    background: none;
}

button.fallback {
    cursor: pointer;
}

.fallback_image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
</style>
